function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h2 className="font-heading text-deep-blue text-xl font-semibold border-b border-[var(--gold)]/30 pb-2">
        {title}
      </h2>
      <div className="space-y-3 text-earth-brown/85 leading-relaxed">{children}</div>
    </div>
  )
}

function Bullets({ items }) {
  return (
    <ul className="list-disc list-outside ml-5 space-y-1">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

export default function Terms() {
  return (
    <section className="parchment-bg min-h-screen pt-28 pb-20 px-4 md:px-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-heading text-deep-blue text-3xl md:text-4xl font-bold mb-2">
          Terms of Service
        </h1>
        <p className="text-earth-brown/50 text-sm mb-10">Last Updated: April 9, 2026</p>

        <div className="bg-soft-white rounded-xl shadow-md p-8 md:p-12 space-y-8">
          <Section title="1. Acceptance of Terms">
            <p>
              These Terms of Service ("Terms") govern your access to and use of Illuminate Faith
              ("Service"), operated by Mason Chapman ("we," "us," or "our") at
              illuminatefaithai.com.
            </p>
            <p>
              By creating an account or using the Service, you agree to be bound by these Terms and
              our Privacy Policy. If you do not agree to these Terms, please do not use our Service.
            </p>
          </Section>

          <Section title="2. Eligibility">
            <p>
              You must be at least 13 years of age to use Illuminate Faith. By using our Service,
              you represent and warrant that you meet this age requirement.
            </p>
            <p>
              If you are between 13 and 18 years of age, you represent that a parent or legal
              guardian has reviewed and agreed to these Terms on your behalf.
            </p>
          </Section>

          <Section title="3. Account Registration">
            <p>You must create an account to access most features. You are responsible for:</p>
            <Bullets
              items={[
                'Providing accurate and truthful information during registration',
                'Maintaining the confidentiality of your login credentials',
                'All activity that occurs under your account',
                'Notifying us immediately of any unauthorized use of your account',
              ]}
            />
            <p>You may not share your account with others or transfer it to another person.</p>
          </Section>

          <Section title="4. Description of Service">
            <p>
              Illuminate Faith provides AI-powered tools designed to help Christians explore
              Scripture, prepare for conversations about faith, and grow spiritually. Features
              include a general faith Q&amp;A chatbot, apologetics assistance, sermon preparation
              tools, and Bible study resources.
            </p>
            <p className="font-medium text-deep-blue/80 bg-[var(--gold)]/10 border border-[var(--gold)]/30 rounded-lg px-4 py-3">
              Important: AI-generated responses are produced by a large language model and are
              intended as a helpful reference tool only — not as definitive theological authority.
              Always verify important theological claims with trusted pastors, scholars, and
              Scripture. Illuminate Faith is a tool to support your faith, not a replacement for
              pastoral guidance, personal Bible study, or the Holy Spirit.
            </p>
          </Section>

          <Section title="5. Subscriptions and Payments">
            <p>
              <strong>Free Tier:</strong> Includes a limited number of daily messages and access to
              general chat features at no cost.
            </p>
            <p>
              <strong>Premium Tier:</strong> Paid subscriptions unlock unlimited messages and
              advanced features. All payments are processed securely through Stripe.
            </p>
            <Bullets
              items={[
                'Monthly subscriptions are billed each month on your signup date',
                'Annual subscriptions are billed once per year',
                'Subscriptions automatically renew unless cancelled before the renewal date',
                'You may cancel at any time through your account settings or by contacting us',
                'Refunds are not provided for partial subscription periods, except where required by applicable law',
                "We reserve the right to change pricing with at least 30 days' notice",
              ]}
            />
          </Section>

          <Section title="6. Acceptable Use">
            <p>You agree not to:</p>
            <Bullets
              items={[
                'Use the Service for any unlawful purpose or in violation of these Terms',
                'Attempt to bypass, circumvent, or exploit message limits or access controls',
                'Use the AI to generate content that is harmful, harassing, defamatory, or discriminatory',
                'Attempt to reverse-engineer, hack, scrape, or otherwise compromise the Service',
                'Use automated bots, scripts, or tools to access the Service without our permission',
                'Impersonate any person, organization, or entity',
                'Upload or transmit malicious code, viruses, or harmful content',
              ]}
            />
            <p>
              We reserve the right to suspend or permanently terminate any account that violates
              these Terms, at our sole discretion, without prior notice.
            </p>
          </Section>

          <Section title="7. AI-Generated Content">
            <p>
              Responses provided by Illuminate Faith are generated by an artificial intelligence
              model. We make no guarantees about the accuracy, completeness, or theological
              correctness of AI-generated content. You use AI-generated content at your own
              discretion and risk.
            </p>
            <p>
              AI-generated content is provided for personal, non-commercial use. You may not
              republish or distribute AI-generated content as your own original work without
              appropriate attribution to Illuminate Faith.
            </p>
          </Section>

          <Section title="8. Intellectual Property">
            <p>
              Illuminate Faith, including its design, code, features, and branding, is owned by
              Mason Chapman and is protected by copyright and other intellectual property laws. You
              may not copy, modify, distribute, or create derivative works from any part of our
              Service without express written permission.
            </p>
            <p>
              Scripture quotations and biblical text are in the public domain unless otherwise
              noted. Any specific translations used remain the property of their respective
              copyright holders.
            </p>
          </Section>

          <Section title="9. Privacy">
            <p>
              Your use of the Service is also governed by our{' '}
              <a href="/privacy" className="text-[var(--gold)] hover:underline">
                Privacy Policy
              </a>
              , which is incorporated by reference into these Terms. Please review it to understand
              how we collect and use your information.
            </p>
          </Section>

          <Section title="10. Disclaimer of Warranties">
            <p>
              The Service is provided "as is" and "as available" without warranties of any kind,
              express or implied. We do not warrant that:
            </p>
            <Bullets
              items={[
                'The Service will be uninterrupted, error-free, or available at all times',
                'AI responses will be accurate, complete, or theologically sound',
                'The Service will meet your specific requirements or expectations',
                'Any errors or defects will be corrected',
              ]}
            />
          </Section>

          <Section title="11. Limitation of Liability">
            <p>
              To the fullest extent permitted by applicable law, Mason Chapman and Illuminate Faith
              shall not be liable for any indirect, incidental, special, consequential, or punitive
              damages arising from your use of — or inability to use — the Service, including any
              reliance on AI-generated content.
            </p>
            <p>
              Our total liability to you for any claim arising from these Terms or your use of the
              Service shall not exceed the total amount you paid us in the 12 months preceding the
              claim.
            </p>
          </Section>

          <Section title="12. Governing Law">
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of
              Texas, without regard to its conflict of law provisions. Any disputes arising from
              these Terms or your use of the Service shall be resolved in the state or federal
              courts located in Texas.
            </p>
          </Section>

          <Section title="13. Changes to These Terms">
            <p>
              We reserve the right to update these Terms at any time. We will notify you of
              significant changes by posting the updated Terms on this page with a revised date.
              Your continued use of the Service after changes are posted constitutes your acceptance
              of the new Terms.
            </p>
          </Section>

          <Section title="14. Contact Us">
            <p>If you have questions about these Terms:</p>
            <Bullets
              items={['Email: support@illuminatefaithai.com', 'Location: Montgomery, Texas']}
            />
          </Section>
        </div>
      </div>
    </section>
  )
}
